// Page-level SEO text that lives in code (never in admin): titles, descriptions,
// intros and FAQs for the public pages. Keep it free of prices, which are
// managed in admin.

const SITE_URL = "https://kinewsstudiolimited.co.uk";
const ADDRESS = "Unit 2, Beechwood House, Milnrow Road, Rochdale, OL16 2AA";

const PAGES = {
  "/": {
    title: "Professional Photographer Rochdale & Manchester | Kinews Studio",
    description:
      "Professional photography studio in Rochdale. Studio portraits, family, birthdays, weddings and events across Rochdale and Manchester. Book your session today.",
    h1: "Professional Photographer & Photography Studio in Rochdale",
    intro:
      "For studio portraits, family shoots, birthdays, weddings and special events in Rochdale and Greater Manchester - we capture genuine moments with care, creativity and attention to detail.",
    faqs: [
      {
        q: "Where is Kinews Studio based?",
        a: `Kinews Studio Limited is based at ${ADDRESS}.`,
      },
      {
        q: "What types of photography do you offer?",
        a: "We provide portrait, family, birthday, event, wedding, headshot and other professional photography services. [Contact us](/contact) if you have a particular type of shoot in mind.",
      },
      {
        q: "Do you photograph events outside Rochdale?",
        a: "Yes. We can provide event photography across Greater Manchester and travel to other UK locations by arrangement. Please [contact us](/contact) with your event date and location to check availability.",
      },
      {
        q: "Do you photograph 50th birthday parties?",
        a: "Yes. We photograph 50th birthdays and other milestone celebrations, including surprise parties, dinners and larger birthday events.",
      },
      {
        q: "How do I book a photography session?",
        a: "You can use our [online booking page](/book) for available studio sessions, or [contact Kinews Studio](/contact) directly for weddings, birthdays, events and other bespoke photography requirements.",
      },
      {
        q: "How far in advance should I book?",
        a: "Booking early is recommended, particularly for weddings and weekend events. [Contact us](/contact) as soon as you know your preferred date so we can confirm availability.",
      },
    ],
  },
  "/book": {
    title: "Book a Photography Session in Rochdale | Kinews Studio",
    description:
      "Book a studio photography session in Rochdale. Choose a one-hour or longer portrait, family or maternity session at Kinews Studio Limited.",
    h1: "Book a photography session",
    intro:
      "Our studio sessions suit individual portraits, family shoots, graduations, maternity photography and personal branding. Shorter sessions work well for a single look or quick portraits, while longer sessions give you more time for multiple looks and larger groups.",
  },
  "/packages": {
    title: "Event Photography Packages Manchester | Kinews Studio",
    // No prices on purpose: package prices are edited in admin.
    description:
      "Wedding and event photography packages covering weddings, birthdays and special occasions in Rochdale, Manchester and beyond. Kinews Studio Limited.",
    h1: "Event Coverage Packages",
    intro:
      "Photo and video coverage for weddings, birthdays, corporate events and special celebrations. Choose the package that fits your day, add your event details and make payment.",
  },
  "/rentals": {
    title: "Studio Rentals for Photoshoots | Kinews Studio Limited",
    description:
      "Book a fully equipped photography studio for your photoshoots, content creation and creative projects at Kinews Studio Limited.",
    h1: "Rent Our Studio",
    intro:
      "Hire our fully equipped photography studio in Rochdale for your own shoot. Choose how long you need, pick a date and start time, and make payment.",
  },
  "/gallery": {
    title: "Photography Gallery | Studio Portraits in Rochdale | Kinews Studio",
    description:
      "Browse studio portrait, family and creative photography captured at the Kinews Studio photography studio in Rochdale, Greater Manchester.",
    h1: "Gallery",
    intro:
      "A selection of studio portraits, family sessions and maternity shoots captured at our Rochdale studio. Click any photo to view it full size.",
  },
  "/blog": {
    title: "Photography Guides & Stories | Kinews Studio Rochdale",
    description:
      "Photography guides and shoot stories from Kinews Studio in Rochdale: what to wear, how to prepare and how to plan your portrait, family, birthday or wedding photography.",
    h1: "Blog",
    intro:
      "Helpful photography guides and stories from our shoots at Kinews Studio in Rochdale.",
  },
  "/printing": {
    title: "Photo & Business Printing | Kinews Studio Rochdale",
    description:
      "Order professional prints online from Kinews Studio Limited in Rochdale, including photo prints, business cards, flyers and large format banners.",
    h1: "Prints",
    intro:
      "Order professional printing online, from leaflets, flyers and post cards to business cards, stationery and stickers. Customize your order and make payment.",
  },
  "/shop": {
    title: "Frames, Prints & Personalised Gifts | Kinews Studio Limited",
    description:
      "Shop high-quality photo frames, prints and personalised gifts including mugs, t-shirts and more at Kinews Studio Limited.",
    h1: "Shop",
    intro:
      "Browse our custom frames, photo prints, posters, canvas and personalised gifts. Choose a product, customise and order directly online.",
  },
  "/contact": {
    title: "Contact Kinews Studio Limited",
    description:
      "Get in touch with Kinews Studio Limited for bookings, events, studio rentals and enquiries. We are here to help you capture your moments.",
    h1: "Get in touch",
    intro:
      "Have a question about a booking, an event or a custom request? Send us a message, give us a call or visit the studio in Rochdale. We typically reply within 24 hours.",
  },
  "/portrait-photographer-rochdale": {
    title: "Portrait Photographer Rochdale | Kinews Studio",
    description:
      "Professional portrait photography at our Rochdale studio. Individual portraits, personal branding and graduation photographs in a relaxed, guided session. Book online.",
    h1: "Portrait Photographer in Rochdale",
    intro:
      "Step into our Rochdale photography studio for a relaxed and professionally guided portrait session. Whether you want individual portraits, personal branding photographs or graduation pictures, we help you feel comfortable and confident in front of the camera.",
    serviceName: "Portrait photography",
    faqs: [
      {
        q: "Where do portrait sessions take place?",
        a: `Portrait sessions take place at our studio: ${ADDRESS}.`,
      },
      {
        q: "How do I book a portrait session?",
        a: "Use our [online booking page](/book) to choose a session length, date and time. If you have a particular requirement, [contact us](/contact) and we will help.",
      },
      {
        q: "Can I book portraits for graduation or personal branding?",
        a: "Yes. We photograph individual portraits, personal branding and graduation pictures. Let us know what the images are for when you [book](/book).",
      },
    ],
  },
  "/family-photographer-rochdale": {
    title: "Family Photographer Rochdale | Kinews Studio",
    description:
      "Relaxed family photography at our Rochdale studio. Natural family portraits and generational photographs you will treasure. Book your family session online.",
    h1: "Family Photographer in Rochdale",
    intro:
      "Families change quickly, but photographs let those moments live on. Our family sessions at our Rochdale studio are designed to capture genuine connections, personalities and memories in a relaxed environment.",
    serviceName: "Family photography",
    faqs: [
      {
        q: "Where do family sessions take place?",
        a: `Family sessions take place at our studio: ${ADDRESS}.`,
      },
      {
        q: "How do I book a family photography session?",
        a: "Use our [online booking page](/book) to choose a session length, date and time. If you have a larger group or a special request, [contact us](/contact) first.",
      },
      {
        q: "Can we coordinate our outfits?",
        a: "Yes. Many families choose matching or coordinated outfits. We are happy to talk through ideas before your session.",
      },
    ],
  },
  "/photography-studio-rochdale": {
    title: "Photography Studio Rochdale | Kinews Studio",
    description:
      "Kinews Studio is a professional photography studio in Rochdale for portraits, family, maternity and personal photoshoots. Book a session or hire the studio.",
    h1: "Photography Studio in Rochdale",
    intro:
      "Kinews Studio is a professional photography studio in Rochdale, Greater Manchester, giving you a dedicated environment for portraits, family sessions, maternity shoots and more.",
    serviceName: "Photography studio sessions",
    faqs: [
      {
        q: "Where is the photography studio?",
        a: `Kinews Studio is at ${ADDRESS}.`,
      },
      {
        q: "Can I hire the studio for my own photoshoot?",
        a: "Yes. You can rent the studio by the hour for your own photoshoot. See our [studio rentals page](/rentals) for options and availability.",
      },
      {
        q: "How do I book a studio photography session?",
        a: "Use our [online booking page](/book) to choose a session length, date and time, then pay online to confirm.",
      },
    ],
  },
};


module.exports = { SITE_URL, ADDRESS, PAGES };
